import { ASSETS, getAsset, REFERENCE_REF } from "./catalogue";
import type { Asset, ScoredAsset } from "./types";

const PINNED: Record<string, Record<string, number>> = {
  "N-2022-0847": {
    "N-2022-0847": 97,
    "N-2023-1104": 94,
    "N-2021-0392": 91,
    "N-2024-0071": 88,
    "B-2022-0510": 74,
    "E-2022-0301": 69,
    "N-2023-0288": 63,
    "A-2023-0918": 58,
  },
};

const PINNED_AFFINE: Record<string, string[]> = {
  "N-2022-0847": [
    "N-2023-1104",
    "N-2021-0392",
    "N-2024-0071",
    "B-2022-0510",
    "E-2022-0301",
  ],
};

const WEIGHT = {
  traits: 0.56,
  category: 0.16,
  material: 0.11,
  acquisition: 0.07,
  mesh: 0.1,
} as const;

function traitOverlap(a: Asset, b: Asset): number {
  const A = new Set(a.traits);
  const B = new Set(b.traits);
  let inter = 0;
  A.forEach((t) => {
    if (B.has(t)) inter++;
  });
  const union = A.size + B.size - inter;
  return union === 0 ? 0 : inter / union;
}

function meshProximity(a: Asset, b: Asset): number {
  const hi = Math.max(a.trianglesM, b.trianglesM);
  const lo = Math.min(a.trianglesM, b.trianglesM);
  return lo / hi;
}

function jitter(from: string, to: string): number {
  const n =
    Math.sin(
      from.length * 12.9898 + (to.codePointAt(to.length - 1) ?? 0) * 78.233,
    ) * 43758.5453;
  return (n - Math.floor(n)) * 2 - 1;
}

function computeMatch(from: Asset, to: Asset): number {
  if (from.ref === to.ref) return 97;

  const raw =
    WEIGHT.traits * traitOverlap(from, to) +
    WEIGHT.category * (from.category === to.category ? 1 : 0) +
    WEIGHT.material * (from.material === to.material ? 1 : 0) +
    WEIGHT.acquisition * (from.acquisition === to.acquisition ? 1 : 0) +
    WEIGHT.mesh * meshProximity(from, to);

  const scaled = 44 + raw * 52 + jitter(from.ref, to.ref) * 1.5;
  return Math.max(41, Math.min(96, Math.round(scaled)));
}

export function matchBetween(fromRef: string, toRef: string): number {
  const pinned = PINNED[fromRef]?.[toRef];
  if (pinned !== undefined) return pinned;
  const from = getAsset(fromRef);
  const to = getAsset(toRef);
  if (!from || !to) return 0;
  return computeMatch(from, to);
}

/* ------------------------------------------------------------------
Result sets
------------------------------------------------------------------ */

export interface ResultSetOptions {
  excludeSelf?: boolean;
  floor?: number;
  limit?: number;
}

function rank(fromRef: string, opts: ResultSetOptions = {}): ScoredAsset[] {
  const { excludeSelf = true, floor = 50, limit = 8 } = opts;

  const pinnedColumn = PINNED[fromRef];
  const pool = pinnedColumn
    ? ASSETS.filter((a) => pinnedColumn[a.ref] !== undefined)
    : ASSETS;

  return pool
    .filter((a) => !(excludeSelf && a.ref === fromRef))
    .map((asset) => ({ asset, match: matchBetween(fromRef, asset.ref) }))
    .filter((r) => r.match >= floor)
    .sort((a, b) => b.match - a.match || a.asset.ref.localeCompare(b.asset.ref))
    .slice(0, limit);
}

export function searchByQuery(query: string): ScoredAsset[] {
  const base = rank(REFERENCE_REF, { excludeSelf: false, floor: 0, limit: 8 });
  const q = query.trim().toLowerCase();
  if (!q) return base;

  const narrowed = base.filter(({ asset }) => {
    if (q.includes("anell") && asset.category !== "anello") return false;
    if (q.includes("platino") && asset.material !== "Platino") return false;
    if (q.includes("collan") && asset.category !== "collana") return false;
    if (q.includes("braccial") && asset.category !== "bracciale") return false;
    if (q.includes("orecchin") && asset.category !== "orecchini") return false;
    return true;
  });

  return narrowed.length ? narrowed : base;
}

export function similarTo(ref: string, limit = 8): ScoredAsset[] {
  return rank(ref, { excludeSelf: true, floor: 41, limit });
}

export function affineFor(ref: string): ScoredAsset[] {
  const curated = PINNED_AFFINE[ref];
  if (curated) {
    return curated
      .map((r) => getAsset(r))
      .filter((a): a is Asset => !!a)
      .map((asset) => ({ asset, match: matchBetween(ref, asset.ref) }));
  }
  return similarTo(ref, 5);
}

export function totalsFor(results: ScoredAsset[]) {
  const rings = results.filter((r) => r.asset.category === "anello").length;
  const size = results.reduce((sum, r) => sum + r.asset.sizeGb, 0);
  const avg = results.length
    ? results.reduce((sum, r) => sum + r.match, 0) / results.length
    : 0;
  return {
    count: results.length,
    rings,
    avgMatch: avg.toFixed(1),
    sizeGb: size.toFixed(2),
  };
}
